import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '~/components/ui/dialog';
import { Input } from '~/components/ui/input';

type ZodIssue = {
  code: string;
  message: string;
  maximum?: number | bigint;
  minimum?: number | bigint;
};

type ParseResult = { success: true } | { success: false; error: { issues: ZodIssue[] } };

type Schema = { safeParse: (data: unknown) => ParseResult };

type Field = {
  name: string;
  label: string;
  placeholder?: string;
  render?: (value: string, onChange: (v: string) => void) => React.ReactNode;
};

type CrudDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  fields: Field[];
  defaultValues?: Record<string, string>;
  schema?: Schema;
  onSubmit: (values: Record<string, string>) => void;
  isPending?: boolean;
  error?: string | null;
  submitLabel?: string;
};

export function CrudDialog({
  open,
  onOpenChange,
  title,
  fields,
  defaultValues,
  schema,
  onSubmit,
  isPending,
  error,
  submitLabel
}: CrudDialogProps): React.ReactNode {
  const { t } = useTranslation();
  const [values, setValues] = useState<Record<string, string>>(defaultValues ?? {});
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setValues(defaultValues ?? {});
      setValidationError(null);
    }
  }, [open, defaultValues]);

  function localizeIssue(issue: ZodIssue): string {
    if (issue.code === 'too_big' && issue.maximum !== undefined) {
      return t('validation.tooLong', { max: Number(issue.maximum) });
    }
    if (issue.code === 'too_small') return t('validation.tooShort');
    if (issue.code === 'invalid_format' || issue.code === 'invalid_string') {
      return t('validation.invalidFormat');
    }
    return t('validation.invalid');
  }

  function handleChange(name: string, value: string): void {
    setValues((prev) => ({ ...prev, [name]: value }));
    setValidationError(null);
  }

  function handleSubmit(e: { preventDefault: () => void }): void {
    e.preventDefault();
    if (schema) {
      const result = schema.safeParse(values);
      if (!result.success) {
        const issue = result.error.issues[0];
        setValidationError(issue ? localizeIssue(issue) : t('validation.invalid'));
        return;
      }
    }
    onSubmit(values);
  }

  const displayError = validationError ?? error;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {fields.map((field) => (
            <div key={field.name} className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground" htmlFor={field.name}>
                {field.label}
              </label>
              {field.render ? (
                field.render(values[field.name] ?? '', (v) => {
                  handleChange(field.name, v);
                })
              ) : (
                <Input
                  id={field.name}
                  placeholder={field.placeholder}
                  value={values[field.name] ?? ''}
                  onChange={(e) => {
                    handleChange(field.name, e.target.value);
                  }}
                />
              )}
            </div>
          ))}
          {displayError && <p className="text-sm text-destructive">{displayError}</p>}
          <DialogFooter>
            <Button type="submit" disabled={isPending}>
              {submitLabel ?? t('actions.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
